import { TimePeriodType } from "@/models/timePeriod.model";
import {
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { getTenantMonthName, tenantLocale, tenantNow, tenantToday, tenantWeekStartsOn } from "@/helpers/tenantCalendar";
import { isMonthFirstLocale } from "@/helpers/tenantDateFormat";

// Periods are computed on demand from the tenant's wall clock, so they never go stale
// after midnight and "today" is the tenant's day rather than the browser's.
export const getTimePeriods = (now: Date = tenantNow()) => {
  const weekOptions = { weekStartsOn: tenantWeekStartsOn() };
  return {
    today: [now, now],
    yesterday: [subDays(now, 1), subDays(now, 1)],
    thisWeek: [startOfWeek(now, weekOptions), now],
    lastWeek: [startOfWeek(subWeeks(now, 1), weekOptions), endOfWeek(subWeeks(now, 1), weekOptions)],
    thisMonth: [startOfMonth(now), now],
    lastMonth: [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
    custom: "Custom Period"
  };
};

export const getPreviousTimePeriods = (now: Date = tenantNow()) => {
  const weekOptions = { weekStartsOn: tenantWeekStartsOn() };
  const { yesterday, lastWeek, lastMonth } = getTimePeriods(now);
  return {
    yesterday,
    dayBeforeYesterday: [subDays(now, 2), subDays(now, 2)],
    lastWeek,
    previousWeek: [startOfWeek(subWeeks(now, 2), weekOptions), endOfWeek(subWeeks(now, 2), weekOptions)],
    lastMonth,
    previousMonth: [startOfMonth(subMonths(now, 2)), endOfMonth(subMonths(now, 2))],
    partialLastWeek: [startOfWeek(subWeeks(now, 1), weekOptions), subWeeks(now, 1)],
    partialLastMonth: [startOfMonth(subMonths(now, 1)), subMonths(now, 1)],
    priorCustom: "Prior Custom Period"
  };
};

export const convertToPriorPeriod = (date?: Date[]): Date[] => {
  if (!date) return [];
  const difference = differenceInCalendarDays(date[1], date[0]);
  return [subDays(date[0], difference + 1), subDays(date[0], 1)];
};

type MonthFormat = 'numeric' | '2-digit' | 'narrow' | 'short' | 'long';

// The parts of a range label, laid out in the tenant locale's order: day first for en-GB
// ("7 Oct 2026", "7 - 9 Oct"), month first for en-US ("Oct 7, 2026", "Oct 7 - 9").
const rangeLayout = (monthFormat: MonthFormat) => {
  const locale = tenantLocale();
  const monthFirst = isMonthFirstLocale(locale);
  const month = (date: Date) => date.toLocaleString(locale, { month: monthFormat });
  const dayMonth = (date: Date) => monthFirst ? `${month(date)} ${date.getDate()}` : `${date.getDate()} ${month(date)}`;
  const withYear = (date: Date) => monthFirst ? `${dayMonth(date)}, ${date.getFullYear()}` : `${dayMonth(date)} ${date.getFullYear()}`;
  // Both ends in one month: the month is written once, beside the day it is next to.
  const daysInMonth = (startDate: Date, endDate: Date) => monthFirst
    ? `${month(startDate)} ${startDate.getDate()} - ${endDate.getDate()}`
    : `${startDate.getDate()} - ${endDate.getDate()} ${month(startDate)}`;
  const daysInMonthWithYear = (startDate: Date, endDate: Date) => monthFirst
    ? `${daysInMonth(startDate, endDate)}, ${startDate.getFullYear()}`
    : `${daysInMonth(startDate, endDate)} ${startDate.getFullYear()}`;
  return { dayMonth, withYear, daysInMonth, daysInMonthWithYear };
};

export const getDisplayDateCondensed = (startDate: Date, endDate: Date): string => {
  const layout = rangeLayout('short');

  return startDate.getMonth() === endDate.getMonth()
    ? layout.daysInMonth(startDate, endDate)
    : `${layout.dayMonth(startDate)} - ${layout.dayMonth(endDate)}`;
};

export const getDisplayDateRange = (
  startDate: Date,
  endDate: Date | undefined | null,
  monthFormat: MonthFormat = 'short',
  includeYear: boolean = false
): string => {
  const layout = rangeLayout(monthFormat);

  if (endDate) {
    if (startDate.getFullYear() !== endDate.getFullYear()) {
      return `${layout.withYear(startDate)} - ${layout.withYear(endDate)}`;
    }
    if (startDate.getMonth() !== endDate.getMonth()) {
      return includeYear
        ? `${layout.withYear(startDate)} - ${layout.withYear(endDate)}`
        : `${layout.dayMonth(startDate)} - ${layout.dayMonth(endDate)}`;
    }
    if (startDate.getDate() !== endDate.getDate()) {
      return includeYear
        ? layout.daysInMonthWithYear(startDate, endDate)
        : layout.daysInMonth(startDate, endDate);
    }
  }

  return includeYear ? layout.withYear(startDate) : layout.dayMonth(startDate);
};

export const getMonthName = (monthNumber: number): string => getTenantMonthName(monthNumber, "long");

export const getTimePeriodTypeDateRange = (
  timePeriodType: TimePeriodType,
  startDate: Date = tenantToday()
): [Date, Date] | undefined => {
  const normalizedStartDate = startOfDay(new Date(startDate));
  const weekOptions = { weekStartsOn: tenantWeekStartsOn() };

  switch (timePeriodType) {
    case TimePeriodType.today:
      return [normalizedStartDate, normalizedStartDate];
    case TimePeriodType.tomorrow:
      const tomorrow = new Date(normalizedStartDate);
      tomorrow.setDate(normalizedStartDate.getDate() + 1);
      return [tomorrow, tomorrow];
    case TimePeriodType.yesterday:
      const yesterday = new Date(normalizedStartDate);
      yesterday.setDate(normalizedStartDate.getDate() - 1);
      return [yesterday, yesterday];
    case TimePeriodType.thisWeek:
      return [startOfWeek(normalizedStartDate, weekOptions), normalizedStartDate];
    case TimePeriodType.lastWeek:
      const lastWeekStart = startOfWeek(subWeeks(normalizedStartDate, 1), weekOptions);
      return [lastWeekStart, startOfDay(endOfWeek(lastWeekStart, weekOptions))];
    case TimePeriodType.thisMonth:
      return [startOfMonth(normalizedStartDate), normalizedStartDate];
    case TimePeriodType.lastMonth:
      const lastMonthStart = startOfMonth(subMonths(normalizedStartDate, 1));
      return [lastMonthStart, startOfDay(endOfMonth(lastMonthStart))];
    case TimePeriodType.fourWeeks:
      const fourWeeksEnd = new Date(normalizedStartDate);
      fourWeeksEnd.setDate(normalizedStartDate.getDate() + 28);
      return [normalizedStartDate, startOfDay(fourWeeksEnd)];
    case TimePeriodType.sixMonths:
      const sixMonthsEnd = new Date(normalizedStartDate);
      sixMonthsEnd.setMonth(normalizedStartDate.getMonth() + 6);
      return [normalizedStartDate, startOfDay(sixMonthsEnd)];
    case TimePeriodType.selectDate:
    case TimePeriodType.custom:
    case TimePeriodType.noEndDate:
    default:
      return undefined;
  }
};

export const getTimePeriodTypeDate = (
  timePeriodType: TimePeriodType,
  startDate: Date = tenantToday()
): Date | undefined => {

  const today = tenantToday();
  const weekOptions = { weekStartsOn: tenantWeekStartsOn() };
  const normalizedStartDate = startOfDay(new Date(startDate));

  let newDate: Date | undefined = undefined;

  switch (timePeriodType) {
    case TimePeriodType.today:
      newDate = today;
      break;
    case TimePeriodType.yesterday:
      newDate = new Date(today);
      newDate.setDate(today.getDate() - 1);
      break;
    case TimePeriodType.tomorrow:
      newDate = new Date(today);
      newDate.setDate(today.getDate() + 1);
      break;
    case TimePeriodType.thisWeek:
      newDate = startOfWeek(today, weekOptions);
      break;
    case TimePeriodType.lastWeek:
      newDate = startOfWeek(subWeeks(today, 1), weekOptions);
      break;
    case TimePeriodType.thisMonth:
      newDate = startOfMonth(today);
      break;
    case TimePeriodType.lastMonth:
      newDate = startOfMonth(subMonths(today, 1));
      break;
    case TimePeriodType.fourWeeks:
      newDate = new Date(normalizedStartDate);
      newDate.setDate(normalizedStartDate.getDate() + 28);
      break;
    case TimePeriodType.sixMonths:
      newDate = new Date(normalizedStartDate);
      newDate.setMonth(normalizedStartDate.getMonth() + 6);
      break;
    case TimePeriodType.selectDate:
    case TimePeriodType.custom:
    case TimePeriodType.noEndDate:
    default:
      break
  }

  return newDate;
};

export const getTimePeriodTypeByDateRange = (
  startDate: Date,
  endDate: Date,
  defaultTimePeriodType: TimePeriodType = TimePeriodType.custom
): TimePeriodType => {
  const normalizedStartDate = startOfDay(new Date(startDate));
  const normalizedEndDate = startOfDay(new Date(endDate));
  const today = tenantToday();

  const isSameRange = (range: [Date, Date] | undefined): boolean => {
    if (!range) return false;

    return startOfDay(range[0]).getTime() === normalizedStartDate.getTime()
      && startOfDay(range[1]).getTime() === normalizedEndDate.getTime();
  };

  const currentDateRanges: Array<[TimePeriodType, [Date, Date] | undefined]> = [
    [TimePeriodType.today, getTimePeriodTypeDateRange(TimePeriodType.today, today)],
    [TimePeriodType.tomorrow, getTimePeriodTypeDateRange(TimePeriodType.tomorrow, today)],
    [TimePeriodType.yesterday, getTimePeriodTypeDateRange(TimePeriodType.yesterday, today)],
    [TimePeriodType.thisWeek, getTimePeriodTypeDateRange(TimePeriodType.thisWeek, today)],
    [TimePeriodType.lastWeek, getTimePeriodTypeDateRange(TimePeriodType.lastWeek, today)],
    [TimePeriodType.thisMonth, getTimePeriodTypeDateRange(TimePeriodType.thisMonth, today)],
    [TimePeriodType.lastMonth, getTimePeriodTypeDateRange(TimePeriodType.lastMonth, today)],
    [TimePeriodType.fourWeeks, getTimePeriodTypeDateRange(TimePeriodType.fourWeeks, normalizedStartDate)],
    [TimePeriodType.sixMonths, getTimePeriodTypeDateRange(TimePeriodType.sixMonths, normalizedStartDate)],
  ];

  for (const [timePeriodType, range] of currentDateRanges) {
    if (isSameRange(range)) {
      return timePeriodType;
    }
  }

  return defaultTimePeriodType;
};

export const getTimePeriodTypeBySingleDate = (
  selectedDate: Date,
  startDate: Date = tenantToday(),
  defaultTimePeriodType: TimePeriodType = TimePeriodType.custom
): TimePeriodType => {
  const normalizedSelectedDate = startOfDay(new Date(selectedDate)).getTime();

  const today = tenantToday();
  const weekOptions = { weekStartsOn: tenantWeekStartsOn() };

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const thisWeekStart = startOfWeek(today, weekOptions);
  const lastWeekStart = startOfWeek(subWeeks(today, 1), weekOptions);
  const thisMonthStart = startOfMonth(today);
  const lastMonthStart = startOfMonth(subMonths(today, 1));

  const fourWeeks = startOfDay(new Date(startDate));
  fourWeeks.setDate(startDate.getDate() + 28);

  const sixMonths = startOfDay(new Date(startDate));
  sixMonths.setMonth(startDate.getMonth() + 6);

  const timePeriodTypesMapping: Array<[TimePeriodType, Date]> = [
    [TimePeriodType.today, today],
    [TimePeriodType.yesterday, yesterday],
    [TimePeriodType.tomorrow, tomorrow],
    [TimePeriodType.thisWeek, thisWeekStart],
    [TimePeriodType.lastWeek, lastWeekStart],
    [TimePeriodType.thisMonth, thisMonthStart],
    [TimePeriodType.lastMonth, lastMonthStart],
    [TimePeriodType.fourWeeks, fourWeeks],
    [TimePeriodType.sixMonths, sixMonths],
  ];

  for (const [timePeriodType, date] of timePeriodTypesMapping) {
    if (normalizedSelectedDate === date.getTime()) {
      return timePeriodType;
    }
  }

  return defaultTimePeriodType;
};